import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import InteractiveString from './InteractiveString';
import NetworkMark from './NetworkMark';

const { FiSend, FiCheck, FiAlertCircle } = FiIcons;

const initialFormState = {
  name: '',
  email: '',
  project: ''
};

const socialLinks = [
  {
    name: 'ChargeNest',
    domain: 'chargenest.de',
    url: 'https://chargenest.de/'
  },
  {
    name: 'Donabox',
    domain: 'donabox.de',
    url: 'https://donabox.de/'
  },
  {
    name: 'Zutatenlupe',
    domain: 'zutatenlupe.com',
    url: 'https://zutatenlupe.com/'
  },
  {
    name: 'PV-Netzplaner',
    domain: 'pv-netzplaner.de',
    url: 'https://pv-netzplaner.de/'
  },
  {
    name: 'LinkedIn',
    domain: 'linkedin.com',
    url: 'https://www.linkedin.com/in/dervis90/'
  },
  {
    name: 'GitHub',
    domain: 'github.com',
    url: 'https://github.com/d3rv15/dervis-bayguel-portfolio'
  }
];

gsap.registerPlugin(ScrollTrigger);

const ContactSection = () => {
  const sectionRef = useRef(null);
  const nameRef = useRef(null);
  const formRef = useRef(null);
  const resetTimerRef = useRef(null);
  const [formState, setFormState] = useState(initialFormState);
  const [status, setStatus] = useState('idle');

  const handleInputChange = (event) => {
    const { name, value } = event.target;

    setFormState((currentState) => ({
      ...currentState,
      [name]: value
    }));
  };

  const resetStatusLater = () => {
    window.clearTimeout(resetTimerRef.current);
    resetTimerRef.current = window.setTimeout(() => {
      setStatus('idle');
    }, 5000);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (status === 'submitting' || !event.currentTarget.checkValidity()) {
      return;
    }

    setStatus('submitting');

    const honeypot = event.currentTarget.elements.hp_contact_extra?.value || '';
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 15000);

    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formState, hp_contact_extra: honeypot }),
        signal: controller.signal
      });

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Unerwartete Serverantwort (kein JSON).');
      }

      const data = await response.json();

      if (!response.ok || data.success !== true) {
        throw new Error(data.message || 'Die Nachricht konnte nicht gesendet werden.');
      }

      setFormState(initialFormState);
      setStatus('success');

      if (formRef.current) {
        const textarea = formRef.current.querySelector('textarea');
        if (textarea) textarea.style.height = 'auto';
      }
    } catch (error) {
      console.error('Kontaktformular konnte nicht gesendet werden:', error);
      setStatus('error');
    } finally {
      window.clearTimeout(timeoutId);
      resetStatusLater();
    }
  };

  useEffect(() => {
    const ctx = gsap.context(() => {
      const letters = nameRef.current?.querySelectorAll('.letter');

      if (letters) {
        gsap.fromTo(
          letters,
          { y: 100, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 1,
            stagger: 0.05,
            ease: 'power4.out',
            scrollTrigger: {
              trigger: sectionRef.current,
              start: 'top 60%',
              end: 'bottom 90%',
              toggleActions: 'play none none reverse'
            }
          }
        );
      }

      gsap.from('.anim-item', {
        y: 30,
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power3.out',
        scrollTrigger: {
          trigger: sectionRef.current,
          start: 'top 70%'
        }
      });
    }, sectionRef);

    return () => {
      ctx.revert();
      window.clearTimeout(resetTimerRef.current);
    };
  }, []);

  const handleNameHover = (event) => {
    const letters = event.currentTarget.querySelectorAll('.letter');

    gsap.to(letters, {
      y: (index) => (index % 2 === 0 ? -15 : 15),
      color: '#ffffff',
      textShadow: '0 0 20px rgba(255,255,255,0.5)',
      duration: 0.4,
      ease: 'power2.out',
      stagger: 0.03
    });
  };

  const handleNameLeave = (event) => {
    const letters = event.currentTarget.querySelectorAll('.letter');

    gsap.to(letters, {
      y: 0,
      color: '#ffffff',
      textShadow: 'none',
      duration: 0.4,
      ease: 'power2.out',
      stagger: 0.03
    });
  };

  const buttonStateClass = status === 'success'
    ? 'bg-green-500/20 border-green-500 text-green-400'
    : status === 'error'
      ? 'bg-red-500/20 border-red-500 text-red-400'
      : 'group-hover:bg-white group-hover:text-black';

  const labelStateClass = status === 'success'
    ? 'text-green-400'
    : status === 'error'
      ? 'text-red-400'
      : 'text-gray-400 group-hover:text-white';

  const buttonText = status === 'submitting'
    ? 'Sendet...'
    : status === 'success'
      ? 'Gesendet!'
      : status === 'error'
        ? 'Erneut versuchen'
        : 'Nachricht senden';

  return (
    <section
      ref={sectionRef}
      id="contact"
      className="relative min-h-screen w-full bg-gradient-to-b from-[#0a0a0a] to-[#020202] flex flex-col justify-between pt-24 px-6 md:px-12 overflow-hidden border-t border-white/5 pb-6"
    >
      <div className="w-full max-w-7xl mx-auto z-10 flex-grow">
        <div className="anim-item mb-16 md:mb-24">
          <h2 className="text-4xl md:text-6xl font-light text-white leading-tight">
            Ein neues Projekt?
            <br />
            <span className="text-gray-500">Lass es uns umsetzen.</span>
          </h2>
        </div>

        <div className="flex flex-col md:flex-row gap-16 md:gap-24">
          <div className="anim-item w-full md:w-3/5 order-2 md:order-1">
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              className="flex flex-col gap-8"
            >
              <input
                type="hidden"
                name="subject"
                value="Neue Anfrage über bayguel.de"
              />
              <input
                type="hidden"
                name="from_name"
                value="Portfolio bayguel.de"
              />
              <input
                type="text"
                name="hp_contact_extra"
                className="hidden"
                tabIndex="-1"
                autoComplete="off"
                aria-hidden="true"
                defaultValue=""
              />

              <div className="group">
                <label
                  htmlFor="contact-name"
                  className="block text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-white transition-colors"
                >
                  Dein Name
                </label>
                <input
                  id="contact-name"
                  type="text"
                  name="name"
                  required
                  minLength="2"
                  maxLength="100"
                  autoComplete="name"
                  value={formState.name}
                  onChange={handleInputChange}
                  placeholder="Max Mustermann"
                  className="w-full bg-transparent border-b border-white/20 py-4 text-xl md:text-2xl text-white placeholder-gray-700 focus:outline-none focus:border-white transition-colors"
                />
              </div>

              <div className="group">
                <label
                  htmlFor="contact-email"
                  className="block text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-white transition-colors"
                >
                  Deine E-Mail
                </label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  required
                  maxLength="254"
                  autoComplete="email"
                  value={formState.email}
                  onChange={handleInputChange}
                  placeholder="max@beispiel.de"
                  className="w-full bg-transparent border-b border-white/20 py-4 text-xl md:text-2xl text-white placeholder-gray-700 focus:outline-none focus:border-white transition-colors"
                />
              </div>

              <div className="group">
                <label
                  htmlFor="contact-project"
                  className="block text-xs uppercase tracking-widest text-gray-500 mb-2 group-focus-within:text-white transition-colors"
                >
                  Erzähl mir von deinem Projekt
                </label>
                <textarea
                  id="contact-project"
                  name="project"
                  required
                  minLength="10"
                  maxLength="5000"
                  value={formState.project}
                  onChange={handleInputChange}
                  placeholder="Hallo, ich möchte eine App bauen..."
                  rows="1"
                  className="w-full bg-transparent border-b border-white/20 py-4 text-xl md:text-2xl text-white placeholder-gray-700 focus:outline-none focus:border-white transition-colors resize-none"
                  onInput={(event) => {
                    event.currentTarget.style.height = 'auto';
                    event.currentTarget.style.height = `${event.currentTarget.scrollHeight}px`;
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="mt-8 self-start flex items-center gap-3 text-white text-lg font-medium group disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className={`w-12 h-12 rounded-full border border-white/20 flex items-center justify-center transition-all duration-300 ${buttonStateClass}`}>
                  {status === 'success' ? (
                    <SafeIcon icon={FiCheck} className="w-5 h-5" />
                  ) : status === 'error' ? (
                    <SafeIcon icon={FiAlertCircle} className="w-5 h-5" />
                  ) : (
                    <SafeIcon
                      icon={FiSend}
                      className="w-5 h-5 -ml-1 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
                    />
                  )}
                </div>

                <span className={`uppercase tracking-widest text-sm transition-colors ${labelStateClass}`}>
                  {buttonText}
                </span>
              </button>

              <p
                className={`min-h-5 text-sm ${
                  status === 'success'
                    ? 'text-green-400'
                    : status === 'error'
                      ? 'text-red-400'
                      : 'text-transparent'
                }`}
                role="status"
                aria-live="polite"
              >
                {status === 'success'
                  ? 'Vielen Dank. Deine Nachricht wurde erfolgreich übermittelt.'
                  : status === 'error'
                    ? 'Die Nachricht konnte nicht gesendet werden. Bitte versuche es erneut oder schreibe an info@bayguel.de.'
                    : '\u00A0'}
              </p>
            </form>
          </div>

          <div className="anim-item w-full md:w-2/5 flex flex-col gap-12 order-1 md:order-2">
            <div className="flex flex-col gap-8">
              <h3 className="text-sm font-medium text-white uppercase tracking-widest border-b border-white/10 pb-4 mb-2">
                Kontakt
              </h3>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 uppercase tracking-widest">
                  E-Mail
                </span>
                <a
                  href="mailto:info@bayguel.de"
                  className="text-lg md:text-xl text-gray-300 hover:text-white transition-colors font-light"
                >
                  info@bayguel.de
                </a>
              </div>

              <div className="flex flex-col gap-1">
                <span className="text-xs text-gray-500 uppercase tracking-widest">
                  Leistungen
                </span>
                <p className="text-sm md:text-base text-gray-400 font-light">
                  Web &amp; App Entwicklung
                  <br />
                  KI Telefonassistenten
                  <br />
                  Elektro- &amp; Solaranlagen
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-4">
              <span className="text-xs text-gray-500 uppercase tracking-widest">
                Netzwerk &amp; Projekte
              </span>
              <div className="flex flex-wrap items-center gap-3">
                {socialLinks.map((link) => (
                  <NetworkMark key={link.name} link={link} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-screen relative left-1/2 -translate-x-1/2 mt-20 md:mt-32 pb-4">
        <InteractiveString />

        <div
          ref={nameRef}
          className="w-full overflow-hidden select-none cursor-default flex justify-center -mt-8 relative z-10 pointer-events-none"
          onMouseEnter={handleNameHover}
          onMouseLeave={handleNameLeave}
        >
          <h1 className="text-[10vw] md:text-[10.5vw] font-black text-white text-center leading-[0.85] tracking-tighter uppercase flex justify-center items-center w-full whitespace-nowrap pointer-events-auto">
            {'DERVIS BAYGÜL'.split('').map((char, index) => (
              <span
                key={`${char}-${index}`}
                className="letter inline-block transform-gpu will-change-transform px-[0.2vw] md:px-[0.4vw]"
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </h1>
        </div>

        <div className="w-full flex justify-center mt-6 md:mt-4 pointer-events-auto">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-6 text-gray-600 font-mono text-xs md:text-sm tracking-[0.15em] uppercase">
            <span>© {new Date().getFullYear()} Dervis Baygül</span>
            <span className="hidden md:inline">|</span>
            <div className="flex gap-4 mt-2 md:mt-0">
              <Link
                to="/impressum"
                className="hover:text-white transition-colors"
              >
                Impressum
              </Link>
              <Link
                to="/datenschutz"
                className="hover:text-white transition-colors"
              >
                Datenschutz
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
